import { supabase } from '../../utils/supabaseClient';

const toDb = (b) => {
  const basePayload = {
    // customer_id intentionally omitted for updates in modify flow; only set during create
    customer_id: b.customerId,
    // awb_no should not be updated in modify flow
    awb_no: b.awbNo,
    date: b.date,
    receiver_name: b.receiverName,
    receiver_address: b.receiverAddress || null,
    pincode: b.pincode || null,
    mode: b.mode,
    type: b.type || null,
    weight: parseFloat(b.weight),
    quantity: parseInt(b.quantity || 1, 10),
    amount: parseFloat(b.amount),
  };

  // Only include other_charge if the column exists (after migration)
  // This prevents errors if the migration hasn't been run yet
  if (b.otherCharge !== undefined) {
    basePayload.other_charge = parseFloat(b.otherCharge || 0);
  }

  return basePayload;
};

export async function getNextAwbNo(userId) {
  const { data, error } = await supabase
    .from('bookings')
    .select('awb_no')
    .eq('user_id', userId)
    .order('awb_no', { ascending: false })
    .limit(1);
  if (error) throw error;
  if (!data || data.length === 0 || !data[0].awb_no) return '000001';
  const last = parseInt(String(data[0].awb_no), 10) || 0;
  const next = last + 1;
  return next.toString().padStart(6, '0');
}

export async function createBooking(userId, booking) {
  const payload = { user_id: userId, ...toDb(booking) };
  const { data, error } = await supabase
    .from('bookings')
    .insert(payload)
    .select('*')
    .single();
  if (error) throw error;
  return data;
}

export async function listBookingsForRange(userId, customerId, fromDate, toDate) {
  let query = supabase
    .from('bookings')
    // Include customer's user-entered customer_id so callers can show/export friendly IDs
    .select('*, customers(customer_id, customer_name)')
    .eq('user_id', userId)
    .order('date', { ascending: true });

  if (customerId) query = query.eq('customer_id', customerId);
  if (fromDate) query = query.gte('date', fromDate);
  if (toDate) query = query.lte('date', toDate);

  const { data, error } = await query;
  if (error) throw error;
  return (data || []).map(row => ({
    ...row,
    customer_name: row.customers?.customer_name || 'Unknown'
  }));
}

export async function getBookingByAwb(userId, awbNo) {
  const { data, error } = await supabase
    .from('bookings')
    .select('*, customers(customer_id, customer_name)')
    .eq('user_id', userId)
    .eq('awb_no', awbNo)
    .single();
  if (error) throw error;
  return data;
}

export async function updateBooking(userId, bookingId, values) {
  // Build payload without customer_id so it is not updated in modify page
  const effectiveValues = { ...values };
  delete effectiveValues.customerId;
  delete effectiveValues.customerIdUuid;
  const full = { ...toDb(effectiveValues) };
  // Ensure customer_id and awb_no are stripped for update
  delete full.customer_id;
  delete full.awb_no;
  const payload = full;
  const { data, error } = await supabase
    .from('bookings')
    .update(payload)
    .eq('user_id', userId)
    .eq('id', bookingId)
    .select('*')
    .single();
  if (error) throw error;
  return data;
}

export async function deleteBooking(userId, bookingId) {
  const { error } = await supabase
    .from('bookings')
    .delete()
    .eq('user_id', userId)
    .eq('id', bookingId);
  if (error) throw error;
}

/**
 * Get recent bookings for a user
 * @param {string} userId - User ID
 * @param {number} limit - Maximum number of bookings to return
 * @returns {Promise<Array>} - Array of recent bookings
 */
export async function getRecentBookings(userId, limit = 5) {
  const { data, error } = await supabase
    .from('bookings')
    .select('*, customers(customer_name)')
    .eq('user_id', userId)
    .order('date', { ascending: false })
    .limit(limit);
  if (error) throw error;

  // Format the data to include customer_name directly in the booking object
  return (data || []).map(booking => ({
    ...booking,
    customer_name: booking.customers?.customer_name || 'Unknown',
    // Add booking_date alias for date to maintain compatibility with the UI
    booking_date: booking.date
  }));
}

/**
 * Create multiple bookings in bulk
 * @param {string} userId - User ID
 * @param {Array} bookings - Array of booking objects
 * @returns {Promise<Object>} - Results with success and failed counts
 */
export async function createMultipleBookings(userId, bookings) {
  if (!bookings || bookings.length === 0) {
    return { success: 0, failed: 0, errors: [] };
  }

  let success = 0;
  let failed = 0;
  const errors = [];

  // Insert one-by-one to avoid entire batch failing on a single bad row (e.g., duplicate AWB)
  // This preserves as many rows as possible for the user.
  for (const booking of bookings) {
    try {
      const base = toDb(booking);
      // Ensure minimally valid payload
      const awb = String(base.awb_no || '').trim();
      const dateStr = String(base.date || '').trim();
      if (!awb || !dateStr) {
        failed += 1;
        errors.push(`Missing AWB or Date for row with AWB '${awb || 'N/A'}'`);
        continue;
      }

      const payload = { user_id: userId, ...base };
      const { error } = await supabase
        .from('bookings')
        .insert(payload)
        .select('*')
        .single();

      if (error) {
        failed += 1;
        // 23505 is unique_violation in Postgres (likely duplicate AWB)
        const msg = error.code === '23505' ? `Duplicate AWB '${awb}'` : error.message || 'Insert failed';
        errors.push(msg);
      } else {
        success += 1;
      }
    } catch (e) {
      failed += 1;
      errors.push(e.message || 'Unknown error');
    }
  }

  return { success, failed, errors };
}


